import React, { useState } from 'react';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import ApiService from '../../components/api_services/apiService.jsx';
import './Calculator.css';

const DEFAULT_DENSITY = 19.32;

function Calculator() {
  const [form, setForm] = useState({
    roz: '',
    szer: '',
    height: '',
    r1: '',
    r2: '',
    densityGPerCm3: String(DEFAULT_DENSITY),
    korektaWagi: '',
    palladGold: false,
    bialyMm: '',
    sumaKMm: '',
    baseMaterialDensityGPerCm3: '13.1',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError(null);
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    const roz = Number(form.roz);
    const szer = Number(form.szer);
    const height = Number(form.height);
    const r1 = Number(form.r1);
    const r2 = Number(form.r2);
    const densityGPerCm3 = form.densityGPerCm3 ? Number(form.densityGPerCm3) : DEFAULT_DENSITY;
    const korektaWagi = form.korektaWagi ? Number(form.korektaWagi) : null;
    const palladGold = Boolean(form.palladGold);
    const bialyMm = form.bialyMm ? Number(form.bialyMm) : null;
    const sumaKMm = form.sumaKMm ? Number(form.sumaKMm) : null;
    const baseMaterialDensityGPerCm3 = form.baseMaterialDensityGPerCm3 ? Number(form.baseMaterialDensityGPerCm3) : null;

    if ([roz, szer, height, r1, r2].some((n) => Number.isNaN(n) || n < 0)) {
      setError('Wprowadź poprawne dodatnie wartości dla wszystkich wymiarów.');
      return;
    }
    if (palladGold) {
      if (bialyMm == null || sumaKMm == null || Number.isNaN(bialyMm) || Number.isNaN(sumaKMm) || bialyMm < 0 || sumaKMm < 0) {
        setError('Dla złota palladowego (pallad) podaj szerokość białego odcinka (bialyMm) i sumę kolorów (sumaKMm) w mm.');
        return;
      }
    }

    setLoading(true);
    try {
      const body = { roz, szer, height, r1, r2 };
      if (densityGPerCm3 > 0 && !Number.isNaN(densityGPerCm3)) {
        body.densityGPerCm3 = densityGPerCm3;
      }
      if (korektaWagi != null && korektaWagi > 0 && !Number.isNaN(korektaWagi)) {
        body.korektaWagi = korektaWagi;
      }
      if (palladGold) {
        body.palladGold = true;
        body.bialyMm = bialyMm;
        body.sumaKMm = sumaKMm;
        if (baseMaterialDensityGPerCm3 != null && baseMaterialDensityGPerCm3 > 0 && !Number.isNaN(baseMaterialDensityGPerCm3)) {
          body.baseMaterialDensityGPerCm3 = baseMaterialDensityGPerCm3;
        }
      }
      const data = await ApiService.ringVolume(body);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Błąd połączenia. Sprawdź połączenie i spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Menu />
      <Content>
        <div className="calculator-page">
          <h1>Kalkulator objętości pierścienia</h1>
          <p className="calculator-description">
            Wprowadź wymiary pierścienia w mm. Objętość w cm³, masa w gramach.
          </p>

          <form className="calculator-form" onSubmit={handleSubmit}>
            <div className="calculator-grid">
              <div className="form-group">
                <label htmlFor="roz">Rozmiar pierścienia (roz)</label>
                <input
                  id="roz"
                  name="roz"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="np. 55"
                  value={form.roz}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="szer">Szerokość (szer) mm</label>
                <input
                  id="szer"
                  name="szer"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="np. 5"
                  value={form.szer}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="height">Wysokość / grubość mm</label>
                <input
                  id="height"
                  name="height"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="np. 2,5"
                  value={form.height}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="r1">Promień R1 (górna krawędź) mm — 0 jeśli płaska</label>
                <input
                  id="r1"
                  name="r1"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="np. 6"
                  value={form.r1}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="r2">Promień R2 (dolna krawędź) mm — 0 jeśli płaska</label>
                <input
                  id="r2"
                  name="r2"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="np. 8"
                  value={form.r2}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="densityGPerCm3">Gęstość g/cm³ (opcjonalnie)</label>
                <input
                  id="densityGPerCm3"
                  name="densityGPerCm3"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="19,32 = złoto czyste"
                  value={form.densityGPerCm3}
                  onChange={handleChange}
                />
                <span className="form-hint">Domyślnie 19,32 (złoto czyste). ~15 dla 750, ~14 dla 585.</span>
              </div>
              <div className="form-group">
                <label htmlFor="korektaWagi">Korekta wagi (opcjonalnie)</label>
                <input
                  id="korektaWagi"
                  name="korektaWagi"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="1,0 = brak korekty"
                  value={form.korektaWagi}
                  onChange={handleChange}
                />
                <span className="form-hint">Domyślnie 1,0. Np. 1,022 dla wzoru A-101 (korekta z wzoru).</span>
              </div>
              <div className="form-group form-group--checkbox">
                <label>
                  <input
                    name="palladGold"
                    type="checkbox"
                    checked={form.palladGold}
                    onChange={handleChange}
                  />
                  {' '}Złoto palladowe (pallad) — korekta wagi
                </label>
                <span className="form-hint">Zastosuj korektę wagi dla białego złota palladowego (wymaga bialyMm i sumaKMm).</span>
              </div>
              {form.palladGold && (
                <>
                  <div className="form-group">
                    <label htmlFor="bialyMm">Szerokość białego odcinka (bialyMm) mm</label>
                    <input
                      id="bialyMm"
                      name="bialyMm"
                      type="number"
                      step="any"
                      min="0"
                      placeholder="np. 4,3"
                      value={form.bialyMm}
                      onChange={handleChange}
                    />
                    <span className="form-hint">Z pasków (paski).</span>
                  </div>
                  <div className="form-group">
                    <label htmlFor="sumaKMm">Suma szerokości kolorów (sumaKMm) mm</label>
                    <input
                      id="sumaKMm"
                      name="sumaKMm"
                      type="number"
                      step="any"
                      min="0"
                      placeholder="np. 9,6"
                      value={form.sumaKMm}
                      onChange={handleChange}
                    />
                    <span className="form-hint">Suma_k (suma kolorów).</span>
                  </div>
                  <div className="form-group">
                    <label htmlFor="baseMaterialDensityGPerCm3">Gęstość materiału bazowego g/cm³ (pallad)</label>
                    <input
                      id="baseMaterialDensityGPerCm3"
                      name="baseMaterialDensityGPerCm3"
                      type="number"
                      step="any"
                      min="0"
                      placeholder="13,1"
                      value={form.baseMaterialDensityGPerCm3}
                      onChange={handleChange}
                    />
                    <span className="form-hint">Domyślnie 13,1 (585). pallad_x = (14,5 / to) − 1.</span>
                  </div>
                </>
              )}
            </div>

            {error && <p className="calculator-error">{error}</p>}

            <button type="submit" className="calculator-submit" disabled={loading}>
              {loading ? 'Obliczanie…' : 'Oblicz objętość i masę'}
            </button>
          </form>

          {result && (
            <div className="calculator-result">
              <h2>Wynik</h2>
              <dl className="result-list">
                <dt>Objętość</dt>
                <dd>{Number(result.volumeCm3).toFixed(4)} cm³</dd>
                <dt>Masa</dt>
                <dd>{Number(result.weightGrams).toFixed(4)} g</dd>
                {result.checkpointPalladGold && (
                  <>
                    <dt>Korekta pallad (checkpoint)</dt>
                    <dd>Tak (waga × pd_x)</dd>
                    {result.pdX != null && (
                      <>
                        <dt>pd_x (współczynnik pallad)</dt>
                        <dd>{Number(result.pdX).toFixed(4)}</dd>
                      </>
                    )}
                  </>
                )}
              </dl>
            </div>
          )}
        </div>
      </Content>
    </>
  );
}

export default Calculator;
